import { Bell, LifeBuoy, Menu, Monitor, Moon, Sun, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SMART_ROUTES } from '../../constants';
import { SMART_GALLERY_NOTICE_FIXTURES } from './SmartGalleryInbox';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from './i18n';
import { useThemeModeStore, type ThemeMode } from './store/useThemeModeStore';
import { cn } from './utils/cn';

const labelByLanguage: Record<SupportedLanguage, string> = {
  en: 'EN',
  es: 'ES',
  fr: 'FR',
};

const iconForMode: Record<ThemeMode, typeof Sun> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

const isSupportedLanguage = (lng: string): lng is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);

const circleBtnClass =
  'border-border bg-bg-elevated text-fg-muted hover:text-fg hover:border-fg-muted/60 relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors focus-visible:ring-accent focus-visible:ring-offset-bg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none';

/** Unfolded stack items — shadow so they read as floating above the page. */
const menuItemClass = `${circleBtnClass} shadow-md`;

export interface SmartHeaderMenuProps {
  /** When false, notifications circle is omitted. Default true in gallery. */
  showNotifications?: boolean;
  unread?: number;
  onNotifications?: () => void;
  onHelp?: () => void;
}

/**
 * Circular hamburger that fans a vertical stack of icon-only circles below it
 * (notifications?, help, language code, theme). Language cycles EN/ES/FR on click.
 */
export function SmartHeaderMenu({
  showNotifications = true,
  unread = 0,
  onNotifications,
  onHelp,
}: SmartHeaderMenuProps) {
  const { t, i18n } = useTranslation('smart');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const mode = useThemeModeStore((s) => s.mode);
  const cycleMode = useThemeModeStore((s) => s.cycleMode);
  const ThemeIcon = iconForMode[mode];

  const active = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const currentLang: SupportedLanguage = isSupportedLanguage(active)
    ? active
    : 'en';
  const nextLang: SupportedLanguage =
    SUPPORTED_LANGUAGES[
      (SUPPORTED_LANGUAGES.indexOf(currentLang) + 1) % SUPPORTED_LANGUAGES.length
    ];

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('touchstart', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('touchstart', onPointer);
    };
  }, [open]);

  const handleNotifications = () => {
    setOpen(false);
    if (onNotifications) {
      onNotifications();
      return;
    }
    navigate(SMART_ROUTES.INBOX);
  };

  const handleHelp = () => {
    setOpen(false);
    onHelp?.();
  };

  const badge =
    showNotifications && unread > 0 ? (
      <span className="bg-accent text-accent-fg absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
        {unread}
      </span>
    ) : null;

  return (
    <div ref={rootRef} className="relative z-40">
      <button
        type="button"
        aria-label={open ? t('notifications.close') : t('headerMenu.open')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className={circleBtnClass}
      >
        {open ? <X size={16} aria-hidden /> : <Menu size={16} aria-hidden />}
        {!open ? badge : null}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('headerMenu.title')}
          className="absolute top-full right-0 mt-2 flex flex-col items-center gap-2"
        >
          {showNotifications ? (
            <button
              type="button"
              role="menuitem"
              aria-label={t('headerMenu.notifications')}
              onClick={handleNotifications}
              className={menuItemClass}
            >
              <Bell size={16} aria-hidden />
              {unread > 0 ? (
                <span className="bg-accent text-accent-fg absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
                  {unread}
                </span>
              ) : null}
            </button>
          ) : null}

          <button
            type="button"
            role="menuitem"
            aria-label={t('common.needHelp')}
            title={t('common.needHelp')}
            onClick={handleHelp}
            className={menuItemClass}
          >
            <LifeBuoy size={16} aria-hidden />
          </button>

          <button
            type="button"
            role="menuitem"
            aria-label={t('headerMenu.language')}
            title={`${labelByLanguage[currentLang]} → ${labelByLanguage[nextLang]}`}
            onClick={() => void i18n.changeLanguage(nextLang)}
            className={cn(menuItemClass, 'text-xs font-semibold tracking-wide')}
          >
            {labelByLanguage[currentLang]}
          </button>

          <button
            type="button"
            role="menuitem"
            aria-label={t('headerMenu.theme')}
            title={t(`theme.${mode}`)}
            onClick={cycleMode}
            className={menuItemClass}
          >
            <ThemeIcon size={16} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Default chrome menu: notifications on, unread from fixtures, inbox route. */
export function SmartGalleryChromeMenu({
  showNotifications = true,
}: {
  showNotifications?: boolean;
} = {}) {
  const navigate = useNavigate();
  const unread = SMART_GALLERY_NOTICE_FIXTURES.filter((item) => !item.read)
    .length;
  return (
    <SmartHeaderMenu
      showNotifications={showNotifications}
      unread={unread}
      onNotifications={() => navigate(SMART_ROUTES.INBOX)}
    />
  );
}
